import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuth } from "@/hooks/useAuth";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { useNavigate } from "react-router";
import { Sparkles, ArrowLeft, Star, Mic2, ScanText, BookOpen, Search, Scale } from "lucide-react";
import { hasPlanConnector, WEBAPP_CONNECTOR } from "@/utils/appAccess";

const FEATURES = [
  {
    key: "ai-assistant",
    title: "סוכן משפטי",
    description: "בינה מלאכותית מתקדמת לשאלות משפטיות",
    icon: <Sparkles className="h-8 w-8 text-indigo-500" />,
    gradient: "from-indigo-500 to-purple-600",
    path: "/assistant",
  },
  {
    key: "recordings",
    title: "הקלטת שיחות",
    description: "הקלטה, תמלול וסיכום משפטי של פגישות",
    icon: <Mic2 className="h-8 w-8 text-sky-500" />,
    gradient: "from-sky-500 to-blue-600",
    path: "/recordings",
  },
  {
    key: "scan-documents-extraction",
    title: "מסמכים",
    description: "חילוץ טקסט ממסמכים סרוקים ו־PDF",
    icon: <ScanText className="h-8 w-8 text-emerald-500" />,
    gradient: "from-emerald-500 to-teal-600",
    path: "/documents",
  },
  {
    key: "word-addon",
    title: "תוסף Word",
    description: "עבודה על מסמכים עם Legalix בתוך Word",
    icon: <BookOpen className="h-8 w-8 text-amber-500" />,
    gradient: "from-amber-500 to-orange-600",
    path: "/knowledge",
  },
  {
    key: "search",
    title: "חיפוש מסמכים",
    description: "בפיתוח",
    icon: <Search className="h-8 w-8 text-violet-500" />,
    gradient: "from-violet-500 to-purple-600",
    path: "/search",
  },
  {
    key: "legal-assistant",
    title: "עוזר משפטי",
    description: "בפיתוח",
    icon: <Scale className="h-8 w-8 text-rose-500" />,
    gradient: "from-rose-500 to-pink-600",
    path: "/legal-assistant",
  },
] as const;

const HomePage = () => {
  const { user } = useAuthContext();
  const auth = useAuth();
  const navigate = useNavigate();
  const userName = (user?.displayName && user.displayName.trim()) || (user?.email ? user.email.split("@")[0] : "לך");
  const accountId = auth.getAccountId();
  const { data: account } = useGetAccountByIdQuery(
    { accountId: accountId! },
    { skip: !accountId },
  );

  const includedAppsIds =
    account?.subscription.plan.includedApps.map((app) => app.appIdentifier) ?? [];
  const showAllFeatures = hasPlanConnector(account, WEBAPP_CONNECTOR);
  const features = showAllFeatures
    ? [...FEATURES]
    : FEATURES.filter((feature) => includedAppsIds.includes(feature.key));

  const handleFeatureClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-indigo-50" dir="rtl">
      {/* Hero Section */}
      <section className="pb-12 pt-16 max-md:pb-8 max-md:pt-20 max-sm:pb-6 max-sm:pt-16">
        <div className="container mx-auto px-6 max-md:px-4">
          <div className="mb-12 text-center">
            <div className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full bg-white/60 px-4 py-2 shadow-lg backdrop-blur-sm">
              <Star className="h-4 w-4 shrink-0 text-yellow-500" />
              <span className="text-xs font-medium text-slate-700 sm:text-sm">פלטפורמת הבינה המלאכותית המשפטית המובילה</span>
            </div>

            <h1 className="text-4xl max-md:text-3xl max-sm:text-2xl font-semibold text-indigo-900 mb-4">
              ברוכים הבאים ל-LegalixAI
            </h1>

            <p className="text-xl max-md:text-lg max-sm:text-base text-slate-600 mb-2">
              היי {userName.split(" ")[0]}, איך נוכל לעזור לך היום?
            </p>

            <p className="text-base max-sm:text-sm text-slate-500 max-w-2xl mx-auto">
              פלטפורמה מתקדמת המביאה את עתיד המשפט לכאן ועכשיו. כלים חכמים, מהירים ויעילים לכל עורך דין.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-8 pb-16 max-md:pt-6 max-md:pb-12 max-sm:pt-4 max-sm:pb-8">
        <div className="container mx-auto px-6 max-md:px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl max-md:text-xl max-sm:text-lg font-semibold text-indigo-900 mb-4">הכלים שלנו</h2>
            <p className="text-base max-sm:text-sm text-slate-600 max-w-xl mx-auto">
              מגוון כלים מתקדמים המיועדים להפוך את העבודה המשפטית ליעילה וחכמה יותר
            </p>
          </div>

          {features.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <Card
                  key={feature.path}
                  className="group bg-white/80 backdrop-blur-sm border-white/80 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => handleFeatureClick(feature.path)}
                >
                  <CardHeader className="pb-4">
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${feature.gradient} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      {feature.icon}
                    </div>
                    <CardTitle className="text-xl max-md:text-lg font-semibold text-indigo-900 group-hover:text-indigo-700 transition-colors">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 mb-4">{feature.description}</CardDescription>
                    <Button
                      variant="ghost"
                      className="w-full text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 group-hover:bg-indigo-50 transition-colors"
                    >
                      <span>התחל עכשיו</span>
                      <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white/80 backdrop-blur-sm border-white/80 shadow-lg max-w-xl mx-auto">
              <CardContent className="p-8 text-center space-y-3">
                <p className="text-slate-700 text-base">
                  אין כרגע אפליקציות פעילות בתוכנית המשרד שלך.
                </p>
                <p className="text-slate-500 text-sm">
                  ניתן לעדכן את פרטי החשבון או לפנות למנהל המשרד להרחבת הגישה.
                </p>
                <Button
                  variant="outline"
                  className="mt-2"
                  onClick={() => navigate("/account")}
                >
                  עבור לחשבון
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {features.length > 0 && (
      <section className="pt-8 pb-16 max-md:pt-6 max-md:pb-12 max-sm:pt-4 max-sm:pb-8">
        <div className="container mx-auto px-6 max-md:px-4">
          <Card className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white border-0 shadow-2xl">
            <CardContent className="p-8 max-md:p-6 text-center">
              <h3 className="text-2xl max-md:text-xl max-sm:text-lg font-semibold mb-4">מוכנים להתחיל?</h3>
              <p className="text-lg max-md:text-base max-sm:text-sm mb-6 opacity-90">
                בחרו בכלי המתאים לכם והתחילו לעבוד בצורה חכמה ויעילה יותר
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-indigo-600 hover:bg-slate-100 shadow-lg"
                  onClick={() => navigate(features[0].path)}
                >
                  <Sparkles className="h-5 w-5 ml-2" />
                  התחל עם הסוכן המשפטי
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="pt-8 pb-6 max-md:pt-6 max-md:pb-4 max-sm:pt-4 max-sm:pb-3">
        <div className="container mx-auto px-6 max-md:px-4">
          <div className="text-center text-slate-500 text-sm">
            <p>© 2024 LegalixAI. כל הזכויות שמורות.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
